<script lang="ts">
import '@sg/src/View/Components/Atoms/Button/button.css';
import '@sg-core/src/View/Components/Atoms/Button/button.css';

import { useI18n } from 'vue-i18n';

import { useEmbeddableMedia } from '@sg-core/src/View/Components/Molecules/VideoPlayer';
import {
  PhxsgBackgroundVideo as PhxsgCoreBackgroundVideo,
  PhxsgBackgroundVideoControls,
  type PhxsgBackgroundVideoProps as PhxsgCoreBackgroundVideoProps,
  type ControlsPosition,
} from '@sg-core/src/View/Components/Organisms/BackgroundVideo';

import { PhxsgIcon } from '../../Atoms/Icon/';
import { PhxsgVideoPlayer } from '../../Molecules/VideoPlayer/';

export type { ControlsPosition };

export interface PhxsgBackgroundVideoProps extends PhxsgCoreBackgroundVideoProps {
  playLabel?: string;
  pauseLabel?: string;
  muteLabel?: string;
  unmuteLabel?: string;
  controlsPosition?: ControlsPosition;
  buttonColor?: 'black' | 'white';
}
</script>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const {
  playLabel,
  pauseLabel,
  controlsPosition = 'top-end',
  buttonColor = 'black',
  embed,
  rootClass,
  initialize = true,
  lazy = true,
  autoplay = true,
  loop = true,
  ...coreProps
} = defineProps<PhxsgBackgroundVideoProps>();

const { t } = useI18n();

const { isYoutube } = useEmbeddableMedia(() => embed);
</script>

<template>
  <PhxsgCoreBackgroundVideo
    v-bind="{ ...coreProps, ...$attrs }"
    :embed="embed ?? undefined"
    :video-player-component="PhxsgVideoPlayer"
    :root-class="rootClass"
    :initialize="initialize"
    :lazy="lazy"
    :autoplay="autoplay"
    :loop="loop"
  >
    <PhxsgBackgroundVideoControls
      :class="{ 'phxsg-theme-dark': buttonColor === 'white' }"
      :position="controlsPosition"
      :loader="!isYoutube"
    >
      <template #play="{ attrs }">
        <PhxsgIcon v-bind="attrs" id="play" size="sm" :title="playLabel ?? t('Accessibility.videoPlay')" />
      </template>

      <template #pause="{ attrs }">
        <PhxsgIcon v-bind="attrs" id="pause" size="sm" :title="pauseLabel ?? t('Accessibility.videoPause')" />
      </template>
    </PhxsgBackgroundVideoControls>

    <slot />
  </PhxsgCoreBackgroundVideo>
</template>

<style src="./background-video.css" />
